import { type MergedCoupon } from '@cart-services/types'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowRightSmall, Money } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useState } from 'react'
import { ScrollableList } from '../../../../components/ScrollableList/ScrollableList'
import { CouponFilterItem } from './CouponFilterItem'
import { DialogCouponOverview } from './DialogCouponOverview'

const styles = stylex.create({
  couponWrapper: {
    alignSelf: 'stretch',
  },
})

interface CouponFilterBarProps {
  tabCoupons: MergedCoupon[]
  selectedCouponIds: Set<number>
  toggleCoupon: (couponId: number) => void
  selectAllTabCoupons: () => void
  deselectAllCoupons: () => void
}

export const CouponFilterBar = ({
  tabCoupons,
  selectedCouponIds,
  toggleCoupon,
  selectAllTabCoupons,
  deselectAllCoupons,
}: CouponFilterBarProps) => {
  const [couponOverviewOpen, setCouponOverviewOpen] = useState(false)
  const hasCoupons = tabCoupons.length > 0
  const hasSelection = selectedCouponIds.size > 0
  const handleToggleAll = hasSelection ? deselectAllCoupons : selectAllTabCoupons

  return (
    <VStack gap={3}>
      <HStack justifyContent="space-between" alignItems="center" gap={2}>
        <HStack gap={2} alignItems="center">
          <Text variant="h5">品牌優惠</Text>
          <Box backgroundImage="linear-gradient(90deg, #FF2E2E 40.87%, #FFBF00 100%)" borderRadius="8px 8px 8px 0" padding="4px 8px" height={20} alignItems="center" display="flex">
            <HStack gap={1} alignItems="center">
              <Box backgroundColor={colors.yellow400} height={14} width={14} display="flex" justifyContent="center" alignItems="center" borderRadius="50%" boxShadow="0px 1px 0px rgba(0, 0, 0, 0.25)">
                <Money color={colors.red600} fontSize={10} />
              </Box>
              <Text color={colors.white} bold noWrap>上萬好康等你發現！</Text>
            </HStack>
          </Box>
        </HStack>
        {hasCoupons && (
          <HStack gap={2} alignItems="baseline">
            {hasSelection
              ? (
                <Text color="grey">
                  已選
                  {selectedCouponIds.size}
                  {' '}
                  張
                </Text>
              )
              : (
                <Text color="grey">看看哪些商品能用券？</Text>
              )}
            <Button
              variant="text"
              size="small"
              color="blue"
              onClick={handleToggleAll}
            >
              {hasSelection ? '取消選取' : '選取全部'}
            </Button>
          </HStack>
        )}
      </HStack>
      {hasCoupons
        ? (
          <>
            <ScrollableList>
              {tabCoupons.map(coupon => (
                <HStack key={coupon.coupon_id} width="300px" minHeight={92} flexShrink={0} rootStyle={styles.couponWrapper}>
                  <CouponFilterItem
                    coupon={coupon}
                    isSelected={selectedCouponIds.has(coupon.coupon_id)}
                    onToggle={toggleCoupon}
                  />
                </HStack>
              ))}
            </ScrollableList>
            <HStack gap={1} alignItems="center" justifyContent="center">
              <Button variant="text" size="small" color="grey" endIcon={<ArrowRightSmall fontSize={14} />} onClick={() => setCouponOverviewOpen(true)}>更多優惠券詳情</Button>
            </HStack>
            <DialogCouponOverview
              open={couponOverviewOpen}
              onClose={() => setCouponOverviewOpen(false)}
              tabCoupons={tabCoupons}
              selectedCouponIds={selectedCouponIds}
              toggleCoupon={toggleCoupon}
              selectAllTabCoupons={selectAllTabCoupons}
              deselectAllCoupons={deselectAllCoupons}
            />
          </>
        )
        : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
            <Text color="grey" align="center">目前沒有品牌優惠券</Text>
          </Box>
        )}
    </VStack>
  )
}
